import React, { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import classNames from "classnames";
import Image from "next/image";
import { useTheme } from "next-themes";
import { CSSTransition } from "react-transition-group";

import { useWindowSize } from "lib/useWindowSize";
import useApiForm from "lib/useApiForm";
import { runAtLeast } from "lib/runAtLeast";
import { signUp, SignUpResult } from "lib/api";
import Navbar from "components/Navbar";
import PageContent from "components/PageContent";
import SharedFooter from "components/SharedFooter";
import ApplicationLayout from "components/ApplicationLayout";
import PageTransitions from "components/PageTransitions";
import StateMessage from "components/StateMessage";
import SocialButton from "components/SocialButton";
import Input from "components/form/Input";
import Submit from "components/form/Submit";
import Slider from "components/Slider";
import ArrowRight from "assets/icons/arrow-right.svg";
import GithubDarkIcon from "assets/icons/github-dark.svg";
import GoogleIcon from "assets/google-icon.svg";
import Logo from "assets/logo.svg";

import Container from "components/Container";

import styles from "./styles.module.css";
import slide1Light from "./slide1-light.png";
import slide2Light from "./slide2-light.png";
import slide3Light from "./slide3-light.png";
import slide1Dark from "./slide1-dark.png";
import slide2Dark from "./slide2-dark.png";
import slide3Dark from "./slide3-dark.png";

import type { Page } from "types/page";

type FormAttributes = "name" | "handle" | "email" | "password";

let blurTimeout: NodeJS.Timeout | null = null;

const SignUp: Page = () => {
  const { theme } = useTheme();
  const { width: windowWidth } = useWindowSize();
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  const [focusMode, setFocusMode] = useState<boolean>(false);

  const initialValues = {
    name: "",
    handle: "",
    email: "",
    password: "",
  };

  const slides = [
    <>
      <div>
        <Image
          alt="Develop and publish extensions"
          priority={true}
          src={theme === "light" ? slide1Light : slide1Dark}
        />
      </div>
      <h3>Develop and publish extensions</h3>
      <p>
        Build extensions for your tools and create your perfect work environment. Publish extensions directly to the
        Store for the community to install.
      </p>
    </>,
    <>
      <div>
        <Image alt="Join and create organizations" priority={true} src={theme === "light" ? slide2Light : slide2Dark} />
      </div>
      <h3>Join and create organizations</h3>
      <p>
        Set up your team and invite members to a shared space to build and install private extensions, and share
        quicklinks and snippets.
      </p>
    </>,
    <>
      <div className={styles.sliderImageWithCompensation}>
        <Image
          alt="Share extensions, quicklinks and snippets"
          priority={true}
          src={theme === "light" ? slide3Light : slide3Dark}
        />
      </div>
      <h3>Share extensions, quicklinks and snippets</h3>
      <p>
        Save time on everyday tasks with shared commands, use Quicklinks for simple bookmarking, and shared Snippets for
        support responses, sharing code and more.
      </p>
    </>,
  ];

  const { values, errors, sending, handleInvalid, handleChange, handleBlur, handleFocus, onSubmit, withServerData } =
    useApiForm<FormAttributes, SignUpResult>({ apiCall, onSuccess, initialValues });

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    // prevent flickering during navigating between input fields
    blurTimeout = setTimeout(() => setInputFocused(false), 100);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e);
    if (blurTimeout) {
      clearTimeout(blurTimeout);
    }
    setInputFocused(true);
  };

  useEffect(() => {
    setFocusMode(inputFocused || Object.values(values).some((value) => !!value));
  }, [values, inputFocused]);

  useEffect(() => {
    if (document !== undefined) {
      document.body.classList.toggle("focus-mode", focusMode);
    }
  }, [focusMode]);

  const handleEscOnPage = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setFocusMode(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleEscOnPage);

    return () => document.removeEventListener("keydown", handleEscOnPage);
  }, [handleEscOnPage]);

  function apiCall(values: Record<FormAttributes, string>): Promise<SignUpResult> {
    return runAtLeast(1300, signUp(values));
  }

  function onSuccess(result: SignUpResult) {
    if ("user" in result) {
      location.href = result.redirect_to;
    }
  }

  const sliderNodeRef = React.useRef(null);
  const formNodeRef = React.useRef(null);

  const isMobile = windowWidth && windowWidth <= 840;

  return (
    <Container>
      <div className={classNames(styles.twoColsWrapper, { [styles.focusMode]: focusMode })}>
        <CSSTransition
          in={isMobile || !focusMode}
          unmountOnExit={!isMobile}
          nodeRef={sliderNodeRef}
          timeout={300}
          classNames={{
            enter: styles.sliderTransitionEnter,
            enterActive: styles.sliderTransitionEnterActive,
            exit: styles.sliderTransitionExit,
            exitActive: styles.sliderTransitionExitActive,
          }}
        >
          <div className={classNames(styles.sliderWrapper)} ref={sliderNodeRef}>
            <Slider slides={slides} autoScrollInterval={3000} title="Start building and collaborating" />
          </div>
        </CSSTransition>
        <CSSTransition
          in={isMobile || !focusMode}
          nodeRef={formNodeRef}
          timeout={300}
          classNames={{
            enter: styles.formTransitionEnter,
            enterActive: styles.formTransitionEnterActive,
            exit: styles.formTransitionExit,
            exitActive: styles.formTransitionExitActive,
          }}
        >
          <div className={styles.controlsWrapper} ref={formNodeRef}>
            <Logo className={styles.logo} />
            <h1 className={styles.header}>Create your account</h1>
            <div className={styles.socialButtonsWrapper}>
              <SocialButton href="/auth/github">
                <GithubDarkIcon className={classNames("icon", styles.socialButtonIcon)} />
              </SocialButton>
              <SocialButton href="/auth/google">
                <GoogleIcon className={classNames("icon", styles.socialButtonIcon)} />
              </SocialButton>
              <p className={styles.orText}>or</p>
            </div>
            <form className={styles.form} action="" onSubmit={onSubmit}>
              <Input
                type="text"
                name="name"
                placeholder="Name"
                error={errors.name || errors.base}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onBlur={onBlur}
                onFocus={onFocus}
                required
                withServerData={withServerData}
              />
              <Input
                type="text"
                name="handle"
                placeholder="Username"
                error={errors.handle}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onBlur={onBlur}
                onFocus={onFocus}
                required
                pattern="^[a-zA-Z0-9\-*~][a-zA-Z0-9\-*._~]+$"
                withServerData={withServerData}
              />
              <Input
                type="email"
                name="email"
                placeholder="Email address"
                error={errors.email}
                autoComplete="email"
                onChange={handleChange}
                onInvalid={handleInvalid}
                onBlur={onBlur}
                onFocus={onFocus}
                required
                withServerData={withServerData}
              />
              <Input
                type="password"
                name="password"
                placeholder="Password (optional)"
                error={errors.password}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onBlur={onBlur}
                onFocus={onFocus}
                withServerData={withServerData}
                autoComplete="current-password"
              />
              {sending ? (
                <StateMessage state="loading" message="Creating account..." />
              ) : (
                <Submit disabled={sending || Object.values(errors).some((error) => !!error)} value={"Create account"} />
              )}
              <p className={styles.terms}>
                By continuing you agree to our&nbsp;
                <a href="https://www.raycast.com/terms-of-service">Terms&nbsp;of&nbsp;Service</a>
              </p>
              <Link href="/users/sign_in">
                <a className={styles.otherOptionLink}>
                  I already have an account&nbsp;
                  <ArrowRight className={classNames("icon", styles.arrowRight)} />
                </a>
              </Link>
            </form>
          </div>
        </CSSTransition>
      </div>
    </Container>
  );
};

SignUp.getLayout = (page) => {
  return (
    <ApplicationLayout>
      <Navbar />
      <PageContent>
        <PageTransitions>{page}</PageTransitions>
      </PageContent>
      <SharedFooter />
    </ApplicationLayout>
  );
};

export default SignUp;
